import { fetchAndDecodeAudio } from './bufferCache';
import { enablePlaybackAudioSession } from './iosAudioSession';
import { clampVolume, loadVolumePrefs, subscribeVolumeChanges } from './volumePrefs';

export interface EditorAudioControllerOptions {
  /** Path under public/, e.g. "/audio/guitar/heroes.mp3". */
  src: string;
  /** Audio tempo — used to convert beats ↔ seconds. Matches chart.bpm. */
  bpm: number;
  /** Milliseconds of intro to skip before beat 0. Maps to ChartAudio.offsetMs. */
  offsetMs: number;
}

/** Inclusive-start, exclusive-end beat window the editor loops over. */
export interface LoopRegionBeats {
  startBeat: number;
  endBeat: number;
}

/**
 * Seek-capable preview playback shared by the Guitar + Pulse map editors.
 *
 * The gameplay {@link import('./scheduler.js').AudioEngine} is purpose-built
 * for a single forward run with a lead-in and loop ramps — it deliberately
 * doesn't expose "jump to an arbitrary beat and play from there", which is
 * exactly what scrubbing a timeline needs. This controller is the editor's
 * lighter-weight counterpart: it decodes the same buffer (via the shared
 * {@link fetchAndDecodeAudio} cache, so no second download) and plays it from
 * any beat — or loops a beat window gaplessly — through a single gain node
 * wired to the player's master × music volume.
 *
 * Two playback modes:
 * - **Forward** ({@link playFrom}) — plays from a beat to the end of the file.
 * - **Loop** ({@link playLoop}) — repeats a `[startBeat, endBeat)` window using
 *   Web Audio's native `loop`/`loopStart`/`loopEnd` for sample-accurate,
 *   click-free repeats, so authors can hear the exact bit they're editing on a
 *   tight cycle while they place notes.
 *
 * It is preview-only and unscored — the authentic feel comes from the real
 * game session launched by each editor's Playtest button.
 */
export class EditorAudioController {
  private readonly src: string;
  private readonly bpm: number;
  private readonly offsetSec: number;

  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private buffer: AudioBuffer | null = null;
  private loadPromise: Promise<AudioBuffer> | null = null;
  private source: AudioBufferSourceNode | null = null;
  private unsubscribeVolume: (() => void) | null = null;

  /** ctx.currentTime captured at the moment playback (re)started. */
  private startedAtCtxTime = 0;
  /** Beat the current playback run began from. */
  private startedAtBeat = 0;
  /** Last beat we settled on while paused/stopped (the scrub head). */
  private pausedBeat = 0;
  private playing = false;
  /** Active loop window while looping, else null (forward / stopped). */
  private loop: LoopRegionBeats | null = null;

  constructor(opts: EditorAudioControllerOptions) {
    this.src = opts.src;
    this.bpm = opts.bpm;
    this.offsetSec = opts.offsetMs / 1000;
  }

  isPlaying(): boolean {
    return this.playing;
  }

  isLooping(): boolean {
    return this.playing && this.loop !== null;
  }

  /**
   * Current playback head in beats. While playing this is derived live from
   * the audio clock (wrapped into the loop window when looping); while paused
   * it's the settled scrub position.
   */
  getCurrentBeat(): number {
    if (!this.playing || !this.ctx) {
      return this.pausedBeat;
    }
    const elapsedSec = this.ctx.currentTime - this.startedAtCtxTime;
    const elapsedBeats = (elapsedSec * this.bpm) / 60;
    if (this.loop) {
      const span = this.loop.endBeat - this.loop.startBeat;
      if (span <= 0) {
        return this.loop.startBeat;
      }
      return this.loop.startBeat + (elapsedBeats % span);
    }
    return this.startedAtBeat + elapsedBeats;
  }

  /**
   * Play from `beat` (defaults to the current scrub head) straight through to
   * the end of the file. Resolves to `true` once audio is actually running, or
   * `false` if there was nothing to play (torn down mid-decode, or the start
   * beat is past the end of the file). Stops any in-flight source first so
   * repeated calls don't stack voices.
   */
  async playFrom(beat?: number): Promise<boolean> {
    const startBeat = Math.max(0, beat ?? this.pausedBeat);
    const buffer = await this.prepareForPlayback();
    if (!buffer) {
      return false;
    }
    const offsetSec = this.offsetSec + (startBeat * 60) / this.bpm;
    // Past the end of the file there's nothing to play — settle the head
    // rather than starting a zero-length source.
    if (offsetSec >= buffer.duration) {
      this.pausedBeat = startBeat;
      this.playing = false;
      this.loop = null;
      return false;
    }
    this.loop = null;
    this.startSource(buffer, offsetSec, null);
    this.startedAtBeat = startBeat;
    this.pausedBeat = startBeat;
    return true;
  }

  /**
   * Loop the `[startBeat, endBeat)` window gaplessly until paused. The window
   * is clamped to the playable span of the buffer. Resolves to the clamped
   * region actually looping (so callers can align their UI with what's heard),
   * or `null` if there was nothing to play. Calling again with a new window
   * restarts the loop with the new bounds.
   */
  async playLoop(region: LoopRegionBeats): Promise<LoopRegionBeats | null> {
    const buffer = await this.prepareForPlayback();
    if (!buffer) {
      return null;
    }
    const secPerBeat = 60 / this.bpm;
    const maxBeat = Math.max(0, (buffer.duration - this.offsetSec) / secPerBeat);
    const startBeat = Math.max(0, Math.min(region.startBeat, maxBeat));
    // Keep at least a sliver so loopStart !== loopEnd (a zero-length native
    // loop never advances).
    const endBeat = Math.max(startBeat + 0.05, Math.min(region.endBeat, maxBeat));
    const loopStartSec = this.offsetSec + startBeat * secPerBeat;
    const loopEndSec = this.offsetSec + endBeat * secPerBeat;
    if (loopStartSec >= buffer.duration) {
      this.playing = false;
      this.loop = null;
      return null;
    }
    this.loop = { startBeat, endBeat };
    this.startSource(buffer, loopStartSec, { start: loopStartSec, end: loopEndSec });
    this.startedAtBeat = startBeat;
    this.pausedBeat = startBeat;
    return this.loop;
  }

  /** Pause playback, leaving the scrub head where the audio currently is. */
  pause(): number {
    if (this.playing) {
      this.pausedBeat = this.getCurrentBeat();
    }
    this.stopSource();
    this.playing = false;
    this.loop = null;
    return this.pausedBeat;
  }

  async destroy(): Promise<void> {
    this.stopSource();
    this.loop = null;
    if (this.unsubscribeVolume) {
      this.unsubscribeVolume();
      this.unsubscribeVolume = null;
    }
    if (this.ctx) {
      try {
        await this.ctx.close();
      } catch (err) {
        console.warn('Failed to close editor AudioContext:', err);
      }
      this.ctx = null;
      this.gain = null;
    }
  }

  /**
   * Shared front half of {@link playFrom} / {@link playLoop}: ensure the buffer
   * is decoded, the context is live (iOS unlock + resume), and any in-flight
   * source is stopped. Returns null if the controller was torn down
   * mid-decode (song change / unmount nulls ctx + gain).
   */
  private async prepareForPlayback(): Promise<AudioBuffer | null> {
    const buffer = await this.ensureBuffer();
    if (!this.ctx || !this.gain) {
      return null;
    }
    // Re-assert playback intent on every unlock so an iOS backgrounding
    // interruption can't leave the preview muted by the silent switch.
    enablePlaybackAudioSession();
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }
    this.stopSource();
    return buffer;
  }

  /** Wire a fresh buffer source (optionally looping) and mark playback live. */
  private startSource(
    buffer: AudioBuffer,
    offsetSec: number,
    loopSec: { start: number; end: number } | null,
  ): void {
    const ctx = this.ctx!;
    const gain = this.gain!;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(gain);
    if (loopSec) {
      source.loop = true;
      source.loopStart = loopSec.start;
      source.loopEnd = loopSec.end;
    }
    source.onended = () => {
      // Ignore the onended that fires because WE swapped the source out
      // (stopSource nulls `this.source` first). Only a natural end here, which
      // a looping source never reaches. The hook's rAF pump notices
      // `!isPlaying()` on the next frame and settles the UI.
      if (this.source !== source) {
        return;
      }
      this.source = null;
      this.playing = false;
      this.loop = null;
      this.pausedBeat = this.beatAtEndOfBuffer();
    };
    source.start(0, Math.max(0, offsetSec));

    this.source = source;
    this.startedAtCtxTime = ctx.currentTime;
    this.playing = true;
  }

  private beatAtEndOfBuffer(): number {
    const buffer = this.buffer;
    if (!buffer) {
      return this.pausedBeat;
    }
    return Math.max(0, ((buffer.duration - this.offsetSec) * this.bpm) / 60);
  }

  private ensureContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.gain = this.ctx.createGain();
      this.gain.connect(this.ctx.destination);
      this.applyVolume();
      this.unsubscribeVolume = subscribeVolumeChanges(() => this.applyVolume());
    }
    return this.ctx;
  }

  private applyVolume(): void {
    if (!this.gain) {
      return;
    }
    const vols = loadVolumePrefs();
    this.gain.gain.value = clampVolume(vols.master) * clampVolume(vols.music);
  }

  private async ensureBuffer(): Promise<AudioBuffer> {
    if (this.buffer) {
      return this.buffer;
    }
    const ctx = this.ensureContext();
    if (!this.loadPromise) {
      this.loadPromise = fetchAndDecodeAudio(ctx, this.src);
    }
    try {
      this.buffer = await this.loadPromise;
    } catch (err) {
      // Drop the rejected promise so a later play retries the decode rather
      // than re-awaiting a permanently-failed one.
      this.loadPromise = null;
      throw err;
    }
    return this.buffer;
  }

  private stopSource(): void {
    if (!this.source) {
      return;
    }
    const source = this.source;
    this.source = null;
    source.onended = null;
    try {
      source.stop();
    } catch (err) {
      // stop() throws if the node already ended on its own; benign here
      // because we've already detached it.
      console.warn('Editor audio source already stopped:', err);
    }
    try {
      source.disconnect();
    } catch (err) {
      console.warn('Failed to disconnect editor audio source:', err);
    }
  }
}
